/**
 * The e-mail design document (Backend/email_designs.py is the source of truth:
 * clean_doc validates it, render turns it into the HTML that is sent).
 */
export type Align = 'left' | 'center';
export type ButtonStyle = 'solid' | 'outline' | 'link';
export type BlockType = 'header' | 'heading' | 'text' | 'callout' | 'buttons' | 'divider' | 'spacer' | 'image' | 'footer';

export interface Theme {
  accent: string; accent_text: string; bg: string; card: string; text: string; muted: string;
  font: string; radius: number;
}
export interface DesignButton { label: string; url: string; style: ButtonStyle; bg: string; color: string }
export interface Block {
  /** Client-side only: a stable React key (the server ignores it). */
  _k?: string;
  type: BlockType;
  align?: Align;
  brand?: string; logo?: string;            // header
  text?: string; size?: 'lg' | 'sm' | number; // heading (lg/sm), spacer (px)
  html?: string; bg?: string;               // text, callout, footer
  buttons?: DesignButton[];                 // buttons
  src?: string; alt?: string; href?: string; // image
}
export interface DesignDoc { theme: Theme; blocks: Block[] }
export interface DesignRow {
  id: string; name: string; description: string; doc: DesignDoc; builtin: boolean;
  based_on?: string; updated_at?: string; updated_by?: string;
}
export interface DesignsMeta {
  builtins: DesignRow[]; saved: DesignRow[];
  fonts: { key: string; label: string }[]; palette: string[]; default_theme: Theme;
  limits: { blocks: number; buttons: number };
  placeholders: { name: string; meaning: string }[];
}

export const BLOCK_LABELS: Record<BlockType, string> = {
  header: 'Header (logo / name)', heading: 'Heading', text: 'Text', callout: 'Highlight box',
  buttons: 'Buttons', divider: 'Divider', spacer: 'Space', image: 'Image', footer: 'Footer',
};

let seq = 0;
export const newKey = () => `b${Date.now().toString(36)}${(seq += 1)}`;

/** Every block with a React key (a doc from the server has none). */
export const withKeys = (doc: DesignDoc): DesignDoc => ({
  theme: { ...doc.theme },
  blocks: (doc.blocks || []).map(b => ({ ...b, _k: b._k || newKey(),
    buttons: b.buttons ? b.buttons.map(x => ({ ...x })) : b.buttons })),
});

/** The doc as the server wants it (no client keys). */
export const forWire = (doc: DesignDoc): DesignDoc => ({
  theme: doc.theme,
  blocks: doc.blocks.map(({ _k, ...rest }) => rest),
});

export const newBlock = (type: BlockType, theme: Theme): Block => {
  const b: Block = { _k: newKey(), type, align: 'left' };
  if (type === 'header') b.brand = 'UniversityTech.io';
  if (type === 'heading') { b.text = 'A heading'; b.size = 'lg'; }
  if (type === 'text') b.html = '<p>Write here. Use the toolbar for <b>bold</b>, <i>italic</i>, links and lists.</p>';
  if (type === 'callout') b.html = '<p><b>Worth knowing:</b> something to stand out.</p>';
  if (type === 'footer') b.html = '<p>You are receiving this because you have an account on UniversityTech.io.</p>';
  if (type === 'buttons') {
    b.buttons = [{ label: 'Start my free trial', url: '{upgrade_url}', style: 'solid', bg: theme.accent, color: theme.accent_text }];
  }
  if (type === 'image') { b.src = ''; b.alt = ''; b.href = ''; }
  return b;
};

/** Plain text (an older custom body) -> paragraphs the editor can show. */
export const textToHtml = (t: string): string => {
  const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return t.replace(/\r\n/g, '\n').split(/\n[ \t]*\n/).map(p => p.trim()).filter(Boolean)
    .map(p => `<p>${esc(p).replace(/\n/g, '<br>')}</p>`).join('');
};

/** Block-level tags: with none, a body is text with (maybe) inline tags. */
export const BLOCK_TAG = /<\/?(?:p|div|table|tbody|thead|tr|td|th|html|body|head|h[1-6]|ul|ol|li|blockquote|pre|center|section|header|footer|hr)(?=[\s/>])[^>]*>/i;
/** Text with inline tags (<b>, <i>, <a> …) -> paragraphs on blank lines and
 *  <br> on single newlines, tags kept — what the server does for such a body. */
export const inlineToBlocks = (t: string): string =>
  t.replace(/\r\n/g, '\n').replace(/(<br\s*\/?>)[ \t]*\n/gi, '$1').split(/\n[ \t]*\n/)
    .map(p => p.replace(/^\n+|\n+$/g, '')).filter(p => p.trim())
    .map(p => `<p>${p.replace(/\n/g, '<br>')}</p>`).join('');
/** Any body (plain text, text with inline tags, or HTML) as editor HTML. */
export const bodyToEditorHtml = (t: string): string => {
  if (!t.trim()) return '';
  if (!/<[a-z][\s\S]*?>/i.test(t)) return textToHtml(t);
  return BLOCK_TAG.test(t) ? t : inlineToBlocks(t);
};

export const URL_OK = /^(?:https?:\/\/|mailto:|\{\{?\s*[A-Za-z_][A-Za-z0-9_ ]*\s*\}\}?)\S*$/i;

/** Hand-off from the library / a past campaign to a new campaign: the
 *  composer takes it when it mounts (or on the event, when already open). */
export const EMAIL_USE_DESIGN_EVENT = 'utech:email-use-design';
export const pendingDesign: { current: { doc: DesignDoc; name: string; subject?: string } | null } = { current: null };
export const sendDesignToCampaign = (doc: DesignDoc, name: string, subject?: string) => {
  pendingDesign.current = { doc, name, subject };
  window.dispatchEvent(new CustomEvent(EMAIL_USE_DESIGN_EVENT));
};
